import { newDateYMD } from 'shared-utils/date-utils'
import { getWeek } from 'date-fns'
import type { ICalendarGridDimensions } from 'shared-types/calendar'

export type { ICalendarGridDimensions, IGridCellPosition } from 'shared-types/calendar'

// Constants
export const DAYS_PER_WEEK = 7
export const MONDAY_OFFSET = 6

/**
 * Calculate the dimensions and structure of the calendar grid
 */
export function calculateCalendarDimensions(year: number, month: number): ICalendarGridDimensions {
  const firstDayOfMonth = newDateYMD(year, month, 1)
  const lastDayOfMonth = newDateYMD(year, month + 1, 0)
  const startingDay = (firstDayOfMonth.getDay() + MONDAY_OFFSET) % DAYS_PER_WEEK
  const numDays = lastDayOfMonth.getDate()
  const numRows = Math.ceil((numDays + startingDay) / DAYS_PER_WEEK)
  const lastDayOfPrevMonth = newDateYMD(year, month, 0)
  const numDaysPrevMonth = lastDayOfPrevMonth.getDate()
  const initialWeek = getWeek(firstDayOfMonth)

  return {
    firstDayOfMonth,
    lastDayOfMonth,
    startingDay,
    numDays,
    numRows,
    numDaysPrevMonth,
    initialWeek,
  }
}

/**
 * Determine what type of cell to render at a given position
 */
export function getCellType(
  rowIndex: number,
  colIndex: number,
  dayCounter: number,
  gridDimensions: ICalendarGridDimensions,
): 'prev-month' | 'current-month' | 'next-month' {
  const { startingDay, numDays } = gridDimensions

  if (rowIndex === 0 && colIndex < startingDay) {
    return 'prev-month'
  }

  if (dayCounter > numDays) {
    return 'next-month'
  }

  return 'current-month'
}

/**
 * Get the day number to display for a given cell
 */
export function getDayNumber(
  cellType: 'prev-month' | 'current-month' | 'next-month',
  colIndex: number,
  dayCounter: number,
  gridDimensions: ICalendarGridDimensions,
): number {
  const { startingDay, numDaysPrevMonth, numDays } = gridDimensions

  if (cellType === 'prev-month') {
    return numDaysPrevMonth - (startingDay - colIndex - 1)
  }

  if (cellType === 'next-month') {
    return dayCounter - numDays
  }

  return dayCounter
}
